import { useEffect } from 'react';
import { useActivityRefreshStore } from '@features/activity';
import { usePicklistStore } from '../store/picklistStore';

/** Owner 2026-10-08: the open picklist is re-read every 30 seconds… */
export const AUTO_REFRESH_EVERY_MS = 30_000;
/** …and on coming back to the tab or the window, unless a read completed in the last few seconds. */
export const RETURN_REFRESH_AFTER_MS = 5_000;
/** How often the page asks. Only asks: the store's coordinator decides, so "every 30 seconds" counts from the LAST
 *  completed read, whatever caused it (an action, a posting poll, "Refresh now"), and never adds a read to one
 *  already running. */
const CHECK_EVERY_MS = 5_000;

/**
 * Keeps the open picklist current for changes made elsewhere — a handheld's Submit, another person's Adjust or Post.
 * Every read goes through the store's one refresh path (`refreshPicklistIfStale`), which is what keeps this, the
 * actions, the posting settle-polls and "Refresh now" from overlapping. Nothing is read while the tab is hidden.
 *
 * When a picklist read starts, the Activity panel re-reads its first page on the same beat, quietly: new entries
 * appear at the top, nothing already loaded is dropped, and no arrival skeleton shows.
 *
 * `picklistId` is null until the picklist is on screen: nothing to keep current before the first load lands.
 */
export function usePicklistAutoRefresh(picklistId: number | null) {
  useEffect(() => {
    if (picklistId === null) return;

    const ask = (maxAgeMs: number) => {
      if (document.visibilityState !== 'visible') return;
      if (usePicklistStore.getState().refreshPicklistIfStale(picklistId, maxAgeMs)) {
        useActivityRefreshStore.getState().refresh();
      }
    };

    const timer = window.setInterval(() => ask(AUTO_REFRESH_EVERY_MS), CHECK_EVERY_MS);
    // Focus and visibility usually fire together on a return; the coordinator joins the second to the first.
    const onReturn = () => ask(RETURN_REFRESH_AFTER_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onReturn();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onReturn);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onReturn);
    };
  }, [picklistId]);
}
